// Wave AW.3-React (2026-05-16) — 외부 바이어 공개 가입 (사업자등록증 업로드)
//
// 백엔드: POST /buyer-app/auth/register-public (multer 'business_cert', 5MB, JPG/PNG/PDF)

import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { LogoFull } from '../Logo';

var COUNTRY_OPTIONS = [
  { code: 'KR', label: '🇰🇷 한국' },
  { code: 'JP', label: '🇯🇵 일본' },
  { code: 'US', label: '🇺🇸 미국' },
  { code: 'CN', label: '🇨🇳 중국' },
  { code: 'RU', label: '🇷🇺 러시아' },
  { code: 'AE', label: '🇦🇪 UAE' },
];

function AuctionBuyerRegister() {
  var [email, setEmail] = useState('');
  var [password, setPassword] = useState('');
  var [name, setName] = useState('');
  var [company, setCompany] = useState('');
  var [phoneE164, setPhoneE164] = useState('');
  var [businessNo, setBusinessNo] = useState('');
  var [businessCountry, setBusinessCountry] = useState('KR');
  var [certFile, setCertFile] = useState(null);
  var [error, setError] = useState('');
  var [loading, setLoading] = useState(false);
  var fileInputRef = useRef(null);
  var navigate = useNavigate();

  // Inline validation — 입력이 있는데 형식 부정인 경우만 에러 메시지 표시 (빈 입력은 untouched)
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var PHONE_RE = /^\+[1-9]\d{6,14}$/;

  var emailErr = email && !EMAIL_RE.test(email)
    ? '올바른 이메일 형식이 아닙니다 (예: name@example.com)'
    : '';
  var passwordErr = password && password.length < 8
    ? '비밀번호는 8자 이상이어야 합니다 (현재 ' + password.length + '자)'
    : '';
  var phoneErr = phoneE164 && !PHONE_RE.test(phoneE164)
    ? '국제 형식이어야 합니다 (예: +821012345678)'
    : '';
  var businessNoErr = businessNo && businessNo.replace(/[^0-9A-Za-z]/g, '').length < 5
    ? '사업자번호가 너무 짧습니다 (숫자/영문 5자 이상)'
    : '';

  var canSubmit = email.indexOf('@') > 0 &&
    EMAIL_RE.test(email) &&
    password.length >= 8 &&
    name.trim().length > 0 &&
    businessNo.trim().length > 0 &&
    !phoneErr &&
    !businessNoErr &&
    certFile != null;

  // UX — 버튼 비활성 사유 안내 (사용자가 무엇이 부족한지 즉시 파악)
  var missingHints = [];
  if (!email || !EMAIL_RE.test(email)) missingHints.push('올바른 이메일');
  if (password.length < 8) missingHints.push('비밀번호 8자 이상');
  if (!name.trim()) missingHints.push('이름');
  if (!businessNo.trim()) missingHints.push('사업자번호');
  if (!certFile) missingHints.push('사업자등록증 파일');

  var handleFileSelect = function(e) {
    setError('');
    var f = e.target.files && e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setError('파일 크기는 5MB 이하여야 합니다');
      return;
    }
    var allowed = ['image/jpeg', 'image/png', 'application/pdf'];
    if (allowed.indexOf(f.type) < 0) {
      setError('JPG, PNG, PDF 만 허용됩니다');
      return;
    }
    setCertFile(f);
  };

  var handleSubmit = function() {
    setError('');
    if (!canSubmit) return;
    setLoading(true);
    var fd = new FormData();
    fd.append('email', email.trim().toLowerCase());
    fd.append('password', password);
    fd.append('name', name.trim());
    if (company.trim()) fd.append('company', company.trim());
    if (phoneE164.trim()) fd.append('phone_e164', phoneE164.trim());
    fd.append('business_no', businessNo.trim());
    fd.append('business_country', businessCountry);
    fd.append('locale', 'ko');
    fd.append('business_cert', certFile);

    fetch(API + '/buyer-app/auth/register-public', {
      method: 'POST',
      body: fd,
    })
      .then(function(r) { return r.json().then(function(d) { return { status: r.status, body: d }; }); })
      .then(function(res) {
        setLoading(false);
        if (res.body && res.body.success) {
          navigate('/buyer/pending');
          return;
        }
        var code = (res.body && res.body.code) || '';
        if (code === 'BUSINESS_BLACKLISTED') {
          setError('차단된 사업자번호입니다. 관리자에게 문의하세요.');
        } else if (code === 'EMAIL_ALREADY_REGISTERED') {
          setError('이미 가입된 이메일입니다.');
        } else if (code === 'PHONE_ALREADY_REGISTERED') {
          setError('이미 가입된 휴대폰입니다.');
        } else if (code === 'FILE_TOO_LARGE') {
          setError('파일 크기는 5MB 이하여야 합니다.');
        } else if (code === 'INVALID_FILE_TYPE') {
          setError('JPG, PNG, PDF 만 허용됩니다.');
        } else if (code === 'PASSWORD_TOO_SHORT') {
          setError('비밀번호는 8자 이상이어야 합니다.');
        } else {
          setError((res.body && res.body.error) || '가입 신청 실패');
        }
      })
      .catch(function() { setLoading(false); setError('서버 연결 실패'); });
  };

  // 인풋 스타일 — 에러 시 빨간 보더 + 옅은 빨강 배경
  var makeInputStyle = function(hasErr) {
    return {
      width: '100%', height: 44, padding: '0 14px',
      border: '1.5px solid ' + (hasErr ? '#fca5a5' : '#e2e8f0'),
      borderRadius: 10, fontSize: 14,
      outline: 'none', transition: 'border-color 0.2s',
      backgroundColor: hasErr ? '#fef2f2' : '#f8fafc',
      fontFamily: "'Pretendard',sans-serif",
      boxSizing: 'border-box',
    };
  };
  var errMsgStyle = {
    fontSize: 12, color: '#dc2626', marginTop: 6,
    display: 'flex', alignItems: 'center', gap: 4,
  };
  var inputStyle = {
    width: '100%', height: 44, padding: '0 14px',
    border: '1.5px solid #e2e8f0', borderRadius: 10, fontSize: 14,
    outline: 'none', transition: 'border-color 0.2s',
    backgroundColor: '#f8fafc', fontFamily: "'Pretendard',sans-serif",
    boxSizing: 'border-box',
  };
  var labelStyle = {
    display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6,
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg,#f8fafc 0%,#eff6ff 100%)',
      fontFamily: "'Pretendard',sans-serif", padding: '40px 20px',
      position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: -200, right: -200, width: 600, height: 600, background: '#dbeafe', borderRadius: '50%', filter: 'blur(100px)', opacity: 0.4, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -200, left: -200, width: 500, height: 500, background: '#e0f2fe', borderRadius: '50%', filter: 'blur(100px)', opacity: 0.4, pointerEvents: 'none' }} />

      <div style={{ maxWidth: 560, margin: '0 auto', position: 'relative', zIndex: 10 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <LogoFull iconSize={36} fontSize={24} />
          <p style={{ color: '#94a3b8', fontSize: 13, fontWeight: 500, letterSpacing: '0.05em', marginTop: 10 }}>USED CAR AUCTION</p>
        </div>

        <div style={{ backgroundColor: 'white', borderRadius: 20, padding: '36px 36px', boxShadow: '0 4px 40px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>회원가입</h2>
          <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 28 }}>사업자등록증을 제출하면 관리자 검토 후 활성화됩니다</p>

          {error && (
            <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#dc2626', fontSize: 16 }}>⚠</span>
              <span style={{ color: '#991b1b', fontSize: 13, fontWeight: 500 }}>{error}</span>
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>이메일 *</label>
            <input type="email" value={email} onChange={function(e){setEmail(e.target.value)}}
              placeholder="buyer@example.com" style={makeInputStyle(!!emailErr)}
            />
            {emailErr && <div style={errMsgStyle}><span>⚠</span><span>{emailErr}</span></div>}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>비밀번호 * (8자 이상)</label>
            <input type="password" value={password} onChange={function(e){setPassword(e.target.value)}}
              style={makeInputStyle(!!passwordErr)}
            />
            {passwordErr && <div style={errMsgStyle}><span>⚠</span><span>{passwordErr}</span></div>}
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>이름 *</label>
            <input type="text" value={name} onChange={function(e){setName(e.target.value)}}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>회사명 (선택)</label>
            <input type="text" value={company} onChange={function(e){setCompany(e.target.value)}}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>휴대폰 (선택, E.164 형식)</label>
            <input type="tel" value={phoneE164} onChange={function(e){setPhoneE164(e.target.value)}}
              placeholder="+821012345678" style={makeInputStyle(!!phoneErr)}
            />
            {phoneErr && <div style={errMsgStyle}><span>⚠</span><span>{phoneErr}</span></div>}
          </div>

          <div style={{ height: 1, background: '#f1f5f9', margin: '8px 0 24px' }} />

          <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>사업자 정보</h3>
          <p style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>차단 회사 재가입 방지를 위해 사업자등록증을 제출해주세요</p>

          <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 3 }}>
              <label style={labelStyle}>사업자번호 *</label>
              <input type="text" value={businessNo} onChange={function(e){setBusinessNo(e.target.value)}}
                placeholder="123-45-67890" style={makeInputStyle(!!businessNoErr)}
              />
              {businessNoErr && <div style={errMsgStyle}><span>⚠</span><span>{businessNoErr}</span></div>}
            </div>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>국가 *</label>
              <select value={businessCountry} onChange={function(e){setBusinessCountry(e.target.value)}}
                style={inputStyle}>
                {COUNTRY_OPTIONS.map(function(o){return <option key={o.code} value={o.code}>{o.label}</option>;})}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>사업자등록증 * (JPG/PNG/PDF, 5MB)</label>
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,application/pdf"
              onChange={handleFileSelect} style={{ display: 'none' }} />
            <button type="button"
              onClick={function(){fileInputRef.current&&fileInputRef.current.click()}}
              style={{
                width: '100%', padding: '14px 16px',
                backgroundColor: certFile ? '#eff6ff' : '#f8fafc',
                border: certFile ? '1.5px solid #3b82f6' : '1.5px dashed #cbd5e1',
                borderRadius: 10, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 12,
                fontFamily: "'Pretendard',sans-serif",
              }}>
              <span style={{ fontSize: 20, color: certFile?'#3b82f6':'#94a3b8' }}>{certFile?'✓':'📎'}</span>
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600, color: certFile?'#1e3a8a':'#64748b', textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {certFile ? certFile.name : '클릭하여 파일 선택'}
              </span>
              {certFile && <span style={{ fontSize: 12, color: '#94a3b8' }}>{(certFile.size/1024).toFixed(0)}KB</span>}
            </button>
          </div>

          <button type="button" onClick={handleSubmit} disabled={!canSubmit||loading}
            style={{
              width: '100%', height: 48,
              background: (!canSubmit||loading) ? '#cbd5e1' : 'linear-gradient(135deg, #1B3A5C 0%, #3b82f6 100%)',
              color: 'white', border: 'none', borderRadius: 12,
              fontSize: 15, fontWeight: 700, cursor: (!canSubmit||loading)?'not-allowed':'pointer',
              fontFamily: "'Pretendard',sans-serif",
              transition: 'all 0.2s',
              boxShadow: (!canSubmit||loading) ? 'none' : '0 4px 12px rgba(27,58,92,0.2)',
            }}>
            {loading ? '신청 중...' : '가입 신청'}
          </button>

          {/* 비활성 사유 안내 — 사용자가 무엇이 부족한지 즉시 표시 */}
          {!canSubmit && !loading && missingHints.length > 0 && (
            <div style={{ marginTop: 10, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, fontSize: 12, color: '#92400e' }}>
              <span style={{ fontWeight: 600 }}>필수 항목: </span>
              {missingHints.join(', ')}
            </div>
          )}

          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
            <span style={{ fontSize: 13, color: '#94a3b8' }}>이미 회원이신가요? </span>
            <button type="button" onClick={function(){navigate('/buyer/login')}}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', fontSize: 13, fontWeight: 700, fontFamily: "'Pretendard',sans-serif" }}>
              로그인
            </button>
          </div>
        </div>

        <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: 12, marginTop: 24 }}>
          © 2026 AUTODEX. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default AuctionBuyerRegister;
